/* Restyle BON's real reward nodes without replacing its redemption handlers. */
if (!customElements.get('iegeek-reward-products')) {
  customElements.define('iegeek-reward-products', class extends HTMLElement {
    connectedCallback() {
      if (!document.body.classList.contains('template-page-iegeek-rewards-program')) return;
      this.cards = new Map();
      this.controller = new AbortController();
      document.addEventListener('click', (event) => {
        if (!event.target.closest?.('#redeem ._tooltip-responsive')) this.closeRules();
      }, { signal: this.controller.signal });
      this.observer = new MutationObserver((mutations) => {
        if (mutations.some(({ target, addedNodes, removedNodes }) =>
          target.closest?.('#redeem') || [...addedNodes, ...removedNodes].some((node) =>
            node.nodeType === 1 && (node.id === 'redeem' || node.querySelector('#redeem'))))) this.schedule();
      });
      this.observe();
      this.schedule();
    }

    observe() {
      this.observer.observe(document.body, { childList: true, subtree: true });
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.observer.disconnect();
        try { this.enhance(); } finally { if (this.isConnected) this.observe(); }
      });
    }

    closeRules() {
      document.querySelectorAll('#redeem [data-iegeek-rules-open]').forEach((tooltip) => {
        tooltip.removeAttribute('data-iegeek-rules-open');
        tooltip.querySelector('.iegeek-reward-product__rules')?.setAttribute('aria-expanded', 'false');
      });
    }

    decorate(card, id, isProduct, override) {
      const title = card.querySelector('.title-block')?.textContent.trim() || '';
      if (this.cards.has(card)) {
        const label = card.querySelector('.iegeek-reward-coupon__value');
        if (label && label.textContent !== title) label.textContent = title;
        return;
      }
      const state = { controller: new AbortController() };
      this.cards.set(card, state);
      card.classList.add('iegeek-reward-card');
      if (isProduct) card.classList.add('iegeek-reward-product');

      const visual = document.createElement('div');
      visual.className = 'iegeek-reward-product__visual';
      visual.setAttribute('aria-hidden', 'true');
      if (override) visual.append(override.content.cloneNode(true));
      else if (!isProduct) {
        const ticket = document.createElement('div');
        ticket.className = 'iegeek-reward-coupon__ticket';
        const brand = document.createElement('span');
        brand.className = 'iegeek-reward-coupon__brand';
        brand.textContent = 'ieGeek';
        const label = document.createElement('span');
        label.className = 'iegeek-reward-coupon__value';
        label.textContent = title;
        ticket.append(brand, label);
        visual.append(ticket);
      } else {
        const fallback = card.querySelector('[class*="bon-loyalty-page-custom-free-product"] img, ._tooltip-responsive .text img') || card.querySelector('.bon-icon-large');
        if (fallback) {
          const image = fallback.cloneNode(true);
          image.removeAttribute('id');
          // Decorative SVG copies must not duplicate BON's gradient IDs.
          image.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
          image.querySelectorAll('[fill^="url("], [stroke^="url("]').forEach((node) => {
            ['fill', 'stroke'].forEach((attribute) => {
              if (node.getAttribute(attribute)?.startsWith('url(')) node.setAttribute(attribute, 'currentColor');
            });
          });
          if (image.tagName === 'IMG') { image.alt = ''; image.loading = 'lazy'; }
          visual.append(image);
        }
      }
      card.prepend(visual);

      const tooltip = card.querySelector('._tooltip-responsive');
      const description = card.querySelector('.bon-desc-btn');
      if (!tooltip || !description) return;
      state.tooltip = tooltip;
      state.marker = document.createComment('BON reward rules position');
      tooltip.before(state.marker);
      const rules = document.createElement('button');
      rules.type = 'button';
      rules.className = 'iegeek-reward-product__rules';
      rules.textContent = this.dataset.rulesLabel || 'Rules';
      rules.setAttribute('aria-expanded', 'false');
      rules.addEventListener('click', (event) => {
        event.stopPropagation();
        const open = !tooltip.hasAttribute('data-iegeek-rules-open');
        this.closeRules();
        tooltip.toggleAttribute('data-iegeek-rules-open', open);
        rules.setAttribute('aria-expanded', String(open));
      }, { signal: state.controller.signal });
      tooltip.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { this.closeRules(); rules.focus(); }
      }, { signal: state.controller.signal });
      tooltip.append(rules);
      description.insertBefore(tooltip, description.querySelector(':scope > .bon-d-flex'));
    }

    enhance() {
      const root = document.getElementById('redeem');
      if (!root) return;
      root.classList.add('iegeek-redemption-enabled');
      for (const [card, state] of this.cards) {
        if (!card.isConnected) { state.controller.abort(); this.cards.delete(card); }
      }
      // Use the public cached payload only to identify reward types. The live
      // BON nodes remain the source of points, conditions and availability.
      const rewards = window.bonLpInfo?.redeemBeforeLogin?.data;
      const types = new Map((Array.isArray(rewards) ? rewards : []).map((reward) => [String(reward.id), Number(reward.redeem_type)]));
      const overrides = new Map([...this.querySelectorAll('template[data-reward-id]')].map((template) => [template.dataset.rewardId, template]));
      let couponCount = 0;
      root.querySelectorAll('#bon-redeem-lists, #bon-redeem-lists-custom').forEach((list) => {
        const products = [];
        let coupons = 0;
        list.querySelectorAll('.bon-card').forEach((card) => {
          const identity = card.querySelector('[id^="tooltip-redeem-"], [id^="btn-redeem-"]');
          const id = identity?.id.match(/^(?:tooltip|btn)-redeem-(\d+)$/)?.[1];
          const isProduct = !!card.querySelector('.title-block-free-product, [class*="bon-loyalty-page-custom-free-product"]') || types.get(id) === 3;
          const item = card.closest('.bon-mb-4');
          if (!item) return;
          if (isProduct) products.push(item);
          else { coupons++; couponCount++; }
          this.decorate(card, id, isProduct, overrides.get(id));
        });
        let couponHeading = list.querySelector(':scope > .iegeek-reward-coupons-heading');
        if (coupons && !couponHeading) {
          couponHeading = document.createElement('h2');
          couponHeading.className = 'iegeek-reward-coupons-heading';
          couponHeading.textContent = this.dataset.couponsHeading || 'Coupons';
          list.prepend(couponHeading);
        } else if (!coupons) couponHeading?.remove();

        let group = list.querySelector(':scope > .iegeek-reward-products-group');
        if (!products.length) { group?.remove(); return; }
        if (!group) {
          group = document.createElement('section');
          group.className = 'iegeek-reward-products-group';
          const heading = document.createElement('h2');
          heading.className = 'iegeek-reward-products-heading';
          heading.textContent = this.dataset.heading || 'Products';
          const grid = document.createElement('div');
          grid.className = 'iegeek-reward-products-grid';
          group.append(heading, grid);
          list.append(group);
        }
        const grid = group.querySelector('.iegeek-reward-products-grid');
        products.forEach((item) => { if (item.parentElement !== grid) grid.append(item); });
        // Keep keyboard order aligned with the coupon / product sections.
        if (group !== list.lastElementChild) list.append(group);
      });
      root.classList.toggle('iegeek-redemption-products-only', couponCount === 0);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      cancelAnimationFrame(this.frame);
      this.frame = null;
      for (const [card, state] of this.cards || []) {
        state.controller.abort();
        card.querySelector('.iegeek-reward-product__visual')?.remove();
        if (state.tooltip) {
          state.tooltip.querySelector('.iegeek-reward-product__rules')?.remove();
          state.tooltip.removeAttribute('data-iegeek-rules-open');
          state.marker.replaceWith(state.tooltip);
        }
        card.classList.remove('iegeek-reward-card', 'iegeek-reward-product');
      }
      this.cards?.clear();
      document.querySelectorAll('#redeem .iegeek-reward-products-group').forEach((group) => {
        group.querySelectorAll('.bon-mb-4').forEach((item) => group.before(item));
        group.remove();
      });
      document.querySelectorAll('#redeem .iegeek-reward-coupons-heading').forEach((heading) => heading.remove());
      document.getElementById('redeem')?.classList.remove('iegeek-redemption-enabled', 'iegeek-redemption-products-only');
    }
  });
}
